import { useEffect, useState, type PropsWithChildren } from "react";
import { cn } from "@/lib/utils";

interface ModalShellProps {
  onClose: () => void;
  modalClassName?: string;
  labelledBy?: string;
}

/**
 * Centered dialog frame: dimmed backdrop, scroll lock, and Escape to close.
 * Shared by Lernkarten and the course completion quiz announcement.
 */
const ModalShell = ({
  onClose,
  modalClassName,
  labelledBy,
  children,
}: PropsWithChildren<ModalShellProps>) => {
  const [mounted, setMounted] = useState(false);

  const revealDialogAfterPaint = () => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  };
  useEffect(revealDialogAfterPaint, []);

  const lockPageScrollWhileDialogIsOpen = () => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  };
  useEffect(lockPageScrollWhileDialogIsOpen, []);

  const closeDialogWhenLearnerPressesEscape = () => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  };
  useEffect(closeDialogWhenLearnerPressesEscape, [onClose]);

  return (
    <div
      className={cn(
        "fixed inset-0 z-100 flex items-center justify-center p-7",
        "bg-linear-to-b from-[#07254a]/42 to-[#07254a]/62 backdrop-blur-[2px]",
        "transition-opacity duration-200",
        mounted ? "opacity-100" : "opacity-0"
      )}
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Dialog schließen"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={cn(
          "relative z-10 w-full flex flex-col overflow-hidden font-sans text-[#0f2647]",
          "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(8,30,60,0.45),0_12px_30px_-10px_rgba(8,30,60,0.25)]",
          "transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]",
          mounted
            ? "translate-y-0 scale-100 opacity-100"
            : "translate-y-2 scale-[0.98] opacity-0",
          modalClassName
        )}
      >
        {children}
      </div>
    </div>
  );
};

export default ModalShell;
